/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef } from 'react'
import * as yup from 'yup'

import { t } from '@/i18n'
import { IReport } from '@/types'
import { ComplaintsReportSourceChoices } from '@/apollo/graphql'
import { useForm } from '@/hooks/index'
import {
  DatePicker,
  FieldViewLink,
  Form,
  FormButtons,
  OptionSelector,
  TextInput,
} from '@/components'
import CarSelector from '@/pages/Car/carSelector'
import {
  addDriverPath,
  editCustomerPath,
  editDealPath,
  editDriverPath,
  generatePath,
  getMaskedString,
} from '@/utils'
import { useSelect } from './useSelect'

interface Props {
  isEditing?: boolean
  initialData?: Partial<IReport>
  loading: boolean
  submitting: boolean
  onCancel: () => void
  onSubmit: (d: IReport) => void
}

const ReportForm: React.FC<Props> = ({
  isEditing,
  initialData,
  loading,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const schema = useMemo(
    () =>
      yup
        .object({
          reportNumber: yup.string().required(t('required_field_error')),
          issueTime: yup.string().required(t('required_field_error')),
          crimeTime: yup.string().required(t('required_field_error')),
          source: yup
            .mixed<ComplaintsReportSourceChoices>()
            .oneOf(
              Object.values(ComplaintsReportSourceChoices),
              t('required_field_error'),
            ),
        })
        .required(),
    [],
  )

  const { reportSourceOptions, locationOptions } = useSelect()

  const { control, isValid, getValues, onTriggerChange, onFormSubmit } =
    useForm<any>({
      isEditing,
      defaultValues: initialData,
      schema,
      onSubmit,
    })

  const formRef = useRef<HTMLFormElement | null>(null)

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  useEffect(() => {
    if (initialData?.car) {
      onTriggerChange('car', initialData.car.id)
    }
  }, [initialData?.car, onTriggerChange])

  useEffect(() => {
    if (initialData?.carNumber) {
      onTriggerChange('carNumber', getMaskedString(initialData.carNumber, true))
    }
  }, [onTriggerChange, initialData?.carNumber])

  const [showDriver, driverLabel] = useMemo(() => {
    const _visible = !isEditing && (initialData?.id || !!initialData?.driver)
    let label = ''

    if (initialData?.driver?.name) {
      label = initialData?.driver?.name
    } else if (initialData?.driver?.email) {
      label = initialData?.driver?.email
    } else if (initialData?.driver?.idNumber) {
      label = initialData?.driver?.idNumber
    } else if (initialData?.driver?.id) {
      label = `${t('driver')} - ${initialData?.driver?.id}`
    }

    return [_visible, label]
  }, [isEditing, initialData?.id, initialData?.driver])

  const [showCustomer, customerLabel] = useMemo(() => {
    const _visible = !isEditing && (initialData?.id || !!initialData?.customer)
    const label =
      initialData?.customer?.name || initialData?.customer?.email || ''

    return [_visible, label]
  }, [isEditing, initialData?.id, initialData?.customer])

  const [showDeal, dealLabel] = useMemo(() => {
    const _visible = !isEditing && (initialData?.id || !!initialData?.deal)
    const label = `${initialData?.deal?.id} - ${initialData?.deal?.status}`

    return [_visible, label]
  }, [isEditing, initialData?.id, initialData?.deal])

  return (
    <div className="edit-info">
      {loading ? (
        <div className="data-box">
          <div className="loading-text">{t('loading')}</div>
        </div>
      ) : (
        <>
          <Form<IReport>
            className={isEditing ? 'editing' : 'view'}
            isEditing={isEditing}
            formRef={formRef}
            control={control}
            onSubmit={onFormSubmit}
          >
            <div className="form-fields">
              <TextInput
                required
                className="w-33"
                control={control}
                name="reportNumber"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('report_number')}
                isEditing={isEditing}
              />

              <CarSelector
                isFormElement
                className="w-33"
                control={control}
                isEditing={isEditing}
                initial={initialData?.car}
                selected={getValues('car')}
                fallbackEl={(props: any) =>
                  initialData?.carNumber ? (
                    <TextInput
                      {...props}
                      isCarNumber
                      name="carNumber"
                      emptyValue={emptyValue}
                      disabled
                      label={t('car_number')}
                    />
                  ) : null
                }
              />
              <OptionSelector
                required
                name="source"
                className="w-33"
                control={control}
                menuPlacement="bottom"
                emptyValue={emptyValue}
                options={reportSourceOptions}
                disabled={!Boolean(isEditing)}
                label={t('source')}
                isEditing={isEditing}
              />
              <OptionSelector
                name="location"
                className="w-33"
                control={control}
                menuPlacement="bottom"
                emptyValue={emptyValue}
                options={locationOptions}
                disabled={!Boolean(isEditing)}
                label={t('location')}
                isEditing={isEditing}
              />
              <DatePicker
                className="w-60"
                name="issueTime"
                showTimeSelect
                control={control}
                timeIntervals={1}
                emptyValue={emptyValue}
                dateFormat="dd/MM/yyyy HH:mm"
                disabled={!Boolean(isEditing)}
                label={t('issue_time')}
                isEditing={isEditing}
              />
              <DatePicker
                className="w-60"
                name="crimeTime"
                showTimeSelect
                control={control}
                timeIntervals={1}
                emptyValue={emptyValue}
                dateFormat="dd/MM/yyyy HH:mm"
                disabled={!Boolean(isEditing)}
                label={t('crime_time')}
                isEditing={isEditing}
              />
              {showCustomer && (
                <FieldViewLink
                  label={t('customer')}
                  linkLabel={customerLabel}
                  link={generatePath(editCustomerPath, {
                    id: initialData?.customer?.id,
                  })}
                />
              )}
              {showDeal && (
                <FieldViewLink
                  label={t('deal')}
                  linkLabel={dealLabel}
                  link={generatePath(editDealPath, {
                    id: initialData?.deal?.id,
                  })}
                />
              )}
              {showDriver && (
                <FieldViewLink
                  label={t('driver')}
                  linkLabel={driverLabel || t('add_driver')}
                  link={generatePath(
                    initialData?.driver?.id ? editDriverPath : addDriverPath,
                    {
                      id: initialData?.driver?.id,
                      carId: initialData?.id,
                    },
                  )}
                />
              )}
            </div>
          </Form>

          <FormButtons
            visible={isEditing}
            formRef={formRef}
            isFormValid={isValid}
            submitting={submitting}
            onCancel={onCancel}
          />
        </>
      )}
    </div>
  )
}

export default ReportForm
